import React, { useEffect, useCallback, memo } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useTicketStore } from '../../store/ticketStore';
import { useShallowStore } from '../../hooks/useShallowStore';
import { SkeletonLine, SkeletonBlock, SkeletonCircle } from '../common/Skeleton.jsx';
import { STATUS_LABELS, assigneeSummary } from './ticketStatus';
import { CommentThread, ActivityTimeline } from './TicketConversation.jsx';
import ProjectTickets, { TicketImages } from './ProjectTickets.jsx';
import { useAuthStore } from '../../store/authStore';
import './Tickets.css';
const PRIORITY_COLORS = { low: '#28a745', medium: '#ffc107', high: '#fd7e14', critical: '#dc3545' };

const clientTicketDetailSelector = (state) => ({
  fetchTicket: state.fetchTicket,
  currentTicket: state.currentTicket,
  addComment: state.addComment,
  loading: state.loading,
});

// Mirrors ClientTicketDetail's layout (header badge/title/meta, description,
// comments, sidebar info card) so the loading state doesn't jump around.
const ClientTicketDetailSkeleton = memo(function ClientTicketDetailSkeleton() {
  return (
    <div className="ticket-detail" aria-hidden="true">
      <div className="ticket-detail-header">
        <SkeletonBlock width={90} height={22} radius={6} style={{ marginBottom: 10 }} />
        <SkeletonLine width="45%" height={22} style={{ marginBottom: 12 }} />
        <div className="ticket-meta-row">
          <SkeletonBlock width={70} height={22} radius={12} />
          <SkeletonBlock width={100} height={22} radius={12} />
        </div>
      </div>

      <div className="ticket-detail-body">
        <div className="ticket-main">
          <section className="ticket-section">
            <SkeletonLine width={110} height={14} style={{ marginBottom: 14 }} />
            <SkeletonLine width="95%" height={12} style={{ marginBottom: 8 }} />
            <SkeletonLine width="80%" height={12} />
          </section>

          <section className="ticket-section">
            <SkeletonLine width={130} height={14} style={{ marginBottom: 14 }} />
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <SkeletonCircle size={28} />
                <SkeletonLine width="60%" height={12} />
              </div>
            ))}
          </section>
        </div>

        <div className="ticket-sidebar">
          <div className="ticket-info-card">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="tk-info-row">
                <SkeletonLine width={70} height={11} />
                <SkeletonLine width={90} height={11} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

function ClientTicketDetail({ ticketId, onBack, backLabel = 'My Tickets' }) {
  const { fetchTicket, currentTicket, addComment, loading } = useShallowStore(useTicketStore, clientTicketDetailSelector);
  const { user } = useAuthStore();

  useEffect(() => { fetchTicket(ticketId); }, [ticketId, fetchTicket]);

  const handleComment = useCallback(
    async (content) => Boolean(await addComment(ticketId, content)),
    [addComment, ticketId]
  );

  if (loading || !currentTicket) return <ClientTicketDetailSkeleton />;

  const t = currentTicket;

  return (
    <div className="ticket-detail">
      <button className="tk-back-btn" onClick={onBack}><FiChevronLeft /> {backLabel}</button>

      <div className="ticket-detail-header">
        <div className="ticket-number-badge">{t.ticketNumber}</div>
        <h2>{t.title}</h2>
        <div className="ticket-meta-row">
          <span className={`priority-badge priority-${t.priority}`}>{t.priority}</span>
          <span className={`tk-status-badge status-${t.status}`}>{STATUS_LABELS[t.status]}</span>
        </div>
      </div>

      <div className="ticket-detail-body">
        <div className="ticket-main">
          <section className="ticket-section">
            <h3>Description</h3>
            <p>{t.description || 'No description provided.'}</p>
            <TicketImages attachments={t.attachments} />
          </section>

          <CommentThread
            comments={t.comments || []}
            myId={String(user?._id || user?.id || '')}
            onSubmit={handleComment}
            placeholder="Write a reply..."
          />

          <ActivityTimeline activity={t.activity || []} title="Progress History" />
        </div>

        <div className="ticket-sidebar">
          <div className="ticket-info-card">
            <div className="tk-info-row"><span>Ticket #</span><strong>{t.ticketNumber}</strong></div>
            <div className="tk-info-row"><span>Project</span><strong>{t.project?.name}</strong></div>
            <div className="tk-info-row"><span>Status</span><strong>{STATUS_LABELS[t.status]}</strong></div>
            <div className="tk-info-row"><span>Priority</span><strong style={{ color: PRIORITY_COLORS[t.priority] }}>{t.priority}</strong></div>
            <div className="tk-info-row"><span>Assigned to</span><strong>{assigneeSummary(t, 3) || 'Pending'}</strong></div>
            <div className="tk-info-row"><span>Created</span><strong>{new Date(t.createdAt).toLocaleDateString()}</strong></div>
            <div className="tk-info-row"><span>Last updated</span><strong>{new Date(t.updatedAt).toLocaleDateString()}</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientPortal() {
  return (
    <ProjectTickets
      renderDetail={(ticketId, onBack, projectName) => (
        <ClientTicketDetail ticketId={ticketId} onBack={onBack} backLabel={projectName} />
      )}
    />
  );
}

export default ClientPortal;
